import { segmentTrack } from '@/hooks/analytics/useTracking';

// Avo tracking plan for the first-build onboarding checklist (CI-6302).
// The standard/pinned fields (event_type, initiator, tracking_type, workspace_slug, …) are
// already set by segmentTrack's baseProperties/baseContext, so only the event-specific
// properties are passed here.

const ONBOARDING_CHECKLIST_NAME = 'first_build_onboarding';
// Bump (major.minor) when the step list, copy, or completion logic changes, so analyses can
// segment behaviour by design iteration.
const ONBOARDING_CHECKLIST_VERSION = 1;

// Stable step ids as defined in the Avo tracking plan — not display titles.
export type OnboardingChecklistStepId = 'connect_repository' | 'get_to_know_ci_config' | 'automate_builds';
export type OnboardingChecklistDisplaySource = 'page_load' | 'onboarding_checklist_launcher';
export type OnboardingChecklistDismissMethod = 'dont_show_again' | 'snooze';

const commonProps = (appSlug: string, totalSteps: number) => ({
  app_slug: appSlug,
  checklist_name: ONBOARDING_CHECKLIST_NAME,
  checklist_version_number: ONBOARDING_CHECKLIST_VERSION,
  checklist_step_total_count: totalSteps,
});

type BaseParams = { appSlug: string; totalSteps: number };
type StepParams = BaseParams & { step: OnboardingChecklistStepId; stepPosition: number };
type ProgressParams = BaseParams & { completedSteps: number };

export function trackChecklistDisplayed({
  appSlug,
  totalSteps,
  completedSteps,
  source,
}: ProgressParams & { source: OnboardingChecklistDisplaySource }): void {
  segmentTrack('Onboarding Checklist Displayed', {
    ...commonProps(appSlug, totalSteps),
    source,
    checklist_completed_steps_count: completedSteps,
  });
}

export function trackChecklistCollapsed({ appSlug, totalSteps, completedSteps }: ProgressParams): void {
  segmentTrack('Onboarding Checklist Collapsed', {
    ...commonProps(appSlug, totalSteps),
    checklist_completed_steps_count: completedSteps,
  });
}

export function trackChecklistItemExpanded({ appSlug, totalSteps, step, stepPosition }: StepParams): void {
  segmentTrack('Onboarding Checklist Item Expanded', {
    ...commonProps(appSlug, totalSteps),
    checklist_step: step,
    checklist_step_position: stepPosition,
  });
}

export function trackChecklistStepStarted({ appSlug, totalSteps, step, stepPosition }: StepParams): void {
  segmentTrack('Onboarding Checklist Step Started', {
    ...commonProps(appSlug, totalSteps),
    checklist_step: step,
    checklist_step_position: stepPosition,
  });
}

export function trackChecklistStepCompleted({ appSlug, totalSteps, step, stepPosition }: StepParams): void {
  segmentTrack('Onboarding Checklist Step Completed', {
    ...commonProps(appSlug, totalSteps),
    checklist_step: step,
    checklist_step_position: stepPosition,
    event_type: 'product',
  });
}

export function trackChecklistFinished({ appSlug, totalSteps, completedSteps }: ProgressParams): void {
  segmentTrack('Onboarding Checklist Finished', {
    ...commonProps(appSlug, totalSteps),
    checklist_completed_steps_count: completedSteps,
    event_type: 'product',
  });
}

// Not wired to UI yet — fires once a dismiss affordance (don't-show-again / snooze) exists.
export function trackChecklistDismissed({
  appSlug,
  totalSteps,
  completedSteps,
  method,
}: ProgressParams & { method: OnboardingChecklistDismissMethod }): void {
  segmentTrack('Onboarding Checklist Dismissed', {
    ...commonProps(appSlug, totalSteps),
    checklist_completed_steps_count: completedSteps,
    checklist_dismiss_method: method,
  });
}
